import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveLine, UnarchiveFilled } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ServiceCode, ServiceCodeToCreate } from '@services/member/list/service-codes/service-codes.types';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useServiceCodesPageVM } from './useServiceCodesPageVM';
import { Formik } from 'formik';

export default function ServiceCodesPage() {
    const {
        mutationHook,
        isArchived,
        handleMetadata,
        handleArchiveServiceCodes,
        updateServiceCode,
        createServiceCode,
        MySwal,
        needReload,
        setIsArchived
    } = useServiceCodesPageVM();
    const columnHelper = createColumnHelper<ServiceCode>();
    const navigate = useNavigate();

    const handleModificationServiceCodes = async (serviceCode: ServiceCodeToCreate | null, id?: number) => {
        await MySwal.fire<ServiceCodeToCreate>({
            title: serviceCode ? "Modification d'un code service" : "Création d'un code service",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: serviceCode ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{ code: serviceCode?.code ?? '', label: serviceCode?.label ?? '' }}
                    onSubmit={(values) => {
                        if (id) {
                            updateServiceCode({
                                id: id,
                                code: values.code,
                                label: values.label
                            });
                        } else {
                            createServiceCode({
                                code: values.code,
                                label: values.label
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-service-code" onSubmit={handleSubmit}>
                            <Input name="code" required label="Code" placeholder="Code" value={values.code} onChange={handleChange} />
                            <Input name="label" required label="Libellé" placeholder="Libellé" value={values.label} onChange={handleChange} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-service-code') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationServiceCodes(
                                    { code: info.row.original.code, label: info.row.original.label },
                                    info.row.original.id
                                );
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchiveServiceCodes([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<ServiceCode>[];

    return (
        <>
            <div className="settings-tabs-content service-codes">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    onRowClick={(row) => navigate(`/members-management/settings/service-codes/${row.id}`)}
                    defaultSorting={{
                        field: 'code',
                        direction: 'desc'
                    }}
                    needReload={needReload}
                    onMetadata={handleMetadata}
                    createButtonOnClick={() => handleModificationServiceCodes(null)}
                    createButtonLabel="Créer un code"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    deleting={{
                        enabled: isArchived,
                        entity: 'service_code'
                    }}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? <UnarchiveLine /> : <ArchiveLine />,
                                hoverIcon: isArchived ? <UnarchiveFilled /> : <ArchiveFilled />,
                                onClick: (lines: ServiceCode[]) => handleArchiveServiceCodes(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
